import { useMemo } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { DEFAULT_PAGE_SIZE } from "@/constants";
import { TCreateLeadPayload, TLead, TLeadsFilters } from "@/models/leads.model";
import { TUser } from "@/models/user.model";
import { TRequestStatus } from "@/models/msc.model";
import leadsService from "@/services/leads.service";

type LeadsState = {
  initialized: boolean;
  entities: Record<number, TLead>;
  pages: Record<number, { ids: number[] }>;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  filters: TLeadsFilters;
  ui: { status: TRequestStatus };
};

type LeadsActions = {
  getLeads: (page?: number) => Promise<TLead[]>;
  applyFiltersAndSearch: (newFilters: TLeadsFilters) => Promise<void>;
  resetFiltersAndSearch: () => Promise<void>;
  // PATCH /lead/:id is declared as answering a TUser in leads.service — the
  // original thunk returned it as-is, so the store keeps that signature.
  updateLeadById: (leadId: number, data: Partial<Omit<TLead, "id">>) => Promise<TUser>;
  createLead: (data: TCreateLeadPayload) => Promise<TLead>;
  fetchLeadById: (leadId: number) => Promise<TLead>;
  deleteLeadById: (leadId: number) => Promise<void>;
};

const INITIAL_FILTERS = { page: 1, pageSize: DEFAULT_PAGE_SIZE, query: "" };

const initialState: LeadsState = {
  initialized: false,
  entities: {},
  pages: {},
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  filters: INITIAL_FILTERS,
  ui: { status: "IDLE" },
};

const currentPageLeadsOf = (state: LeadsState): TLead[] => {
  const rec = state.pages[state.currentPage];
  if (!rec || !rec.ids || rec.ids.length === 0) return [];
  return rec.ids.map((id) => state.entities[id]).filter(Boolean) as TLead[];
};

export const useLeadsStore = create<LeadsState & LeadsActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      getLeads: async (page = 1) => {
        const { pages, filters } = get();
        const cached = pages[page];
        if (cached) {
          set({ currentPage: page }, false, "setCurrentPage"); // if already cached, return directly
          return currentPageLeadsOf(get());
        }

        set({ ui: { status: "PENDING" } }, false, "getLeads/pending");
        const resp = await leadsService.getAll({ ...filters, page });

        const { data: leads, meta } = resp;
        set(
          (state) => {
            const entities = { ...state.entities };
            leads.forEach((lead) => {
              entities[lead.id] = { ...(entities[lead.id] ?? {}), ...lead };
            });
            return {
              entities,
              pages: { ...state.pages, [meta.page]: { ids: leads.map((lead) => lead.id) } },
              currentPage: meta.page,
              totalPages: meta.totalPages,
              totalCount: meta.totalCount,
              initialized: true,
              ui: { status: "IDLE" as TRequestStatus },
            };
          },
          false,
          "getLeads/fulfilled"
        );
        return resp.data;
      },

      applyFiltersAndSearch: async (newFilters) => {
        set(
          (state) => ({
            filters: { ...state.filters, ...newFilters, page: 1 },
            pages: {},
            currentPage: 1,
          }),
          false,
          "applyFiltersAndSearch"
        );
        await get().getLeads(1);
      },

      resetFiltersAndSearch: async () => {
        set(
          { filters: INITIAL_FILTERS, pages: {}, currentPage: 1 },
          false,
          "resetFiltersAndSearch"
        );
        await get().getLeads(1);
      },

      // Optimistic update: merge the changes immediately, restore on API failure.
      updateLeadById: async (leadId, data) => {
        const cachedLead = get().entities[leadId];

        set(
          (state) => ({
            entities: {
              ...state.entities,
              [leadId]: { ...(state.entities[leadId] ?? {}), ...data, id: leadId } as TLead,
            },
            ui: { status: "IDLE" as TRequestStatus },
          }),
          false,
          "updateLeadById/optimistic"
        );

        try {
          const response = await leadsService.update(leadId, data);
          return response;
        } catch (error) {
          set(
            (state) => ({
              entities: { ...state.entities, [leadId]: cachedLead },
              ui: { status: "IDLE" as TRequestStatus },
            }),
            false,
            "updateLeadById/rollback"
          );
          throw error;
        }
      },

      createLead: async (data) => {
        set({ ui: { status: "PENDING" } }, false, "createLead/pending");
        const newLead = await leadsService.createLead(data);
        set(
          (state) => ({
            // TODO: Analize what would be the behavior for the pagination
            entities: { ...state.entities, [newLead.id]: newLead },
            totalCount: state.totalCount + 1,
            ui: { status: "IDLE" as TRequestStatus },
          }),
          false,
          "createLead/fulfilled"
        );
        return newLead;
      },

      fetchLeadById: async (leadId) => {
        const cachedLead = get().entities[leadId];

        // If we already have the full lead data, return it
        if (cachedLead && cachedLead.email !== undefined) {
          return cachedLead;
        }

        // Otherwise fetch from API
        set({ ui: { status: "PENDING" } }, false, "fetchLeadById/pending");
        const lead = await leadsService.getById(leadId);
        set(
          (state) => ({
            entities: {
              ...state.entities,
              [lead.id]: { ...(state.entities[lead.id] ?? {}), ...lead },
            },
            ui: { status: "IDLE" as TRequestStatus },
          }),
          false,
          "fetchLeadById/fulfilled"
        );
        return lead;
      },

      deleteLeadById: async (leadId) => {
        await leadsService.deleteLead(leadId);
        set(
          (state) => {
            const entities = { ...state.entities };
            delete entities[leadId];
            // Remove from all pages
            const pages: LeadsState["pages"] = {};
            Object.keys(state.pages).forEach((page) => {
              const pageNum = parseInt(page);
              pages[pageNum] = {
                ids: state.pages[pageNum].ids.filter((id) => id !== leadId),
              };
            });
            return {
              entities,
              pages,
              totalCount: Math.max(0, state.totalCount - 1),
              ui: { status: "IDLE" as TRequestStatus },
            };
          },
          false,
          "deleteLeadById/fulfilled"
        );
      },
    }),
    { name: "leads" }
  )
);

/** Leads of the page currently in view — replaces the old `selectCurrentPageLeads`. */
export const useCurrentPageLeads = (): TLead[] => {
  const entities = useLeadsStore((s) => s.entities);
  const pages = useLeadsStore((s) => s.pages);
  const currentPage = useLeadsStore((s) => s.currentPage);
  return useMemo(
    () => currentPageLeadsOf({ entities, pages, currentPage } as LeadsState),
    [entities, pages, currentPage]
  );
};
